import React, { useState, useRef, useEffect, useCallback, useContext, ReactNode } from 'react'
import { useRouter } from 'next/router'

import { Modal, Typography, Button, Link, StackView, ActivityIndicator } from '@telus-uds/components-web'
import { HeadingContainer, ScrollContainer, DisclaimerContainer, StackViewContainer, LoadingContainer } from './Styles'
import { SiteBuilderContext } from '@/siteBuilder/renderer/context/SiteBuilderContext'

export type AccessConsentModalProps = {
  heading: string
  bodyContent: ReactNode
  disclaimerText?: ReactNode
  agreeButtonLabel: string
  redirectUrl?: string
}

/** Normalises any locale string to the two values accepted. */
const normalizeLocale = (value?: string | null): 'en' | 'fr' => (value?.toLowerCase().startsWith('fr') ? 'fr' : 'en')

const SCROLL_THRESHOLD = 10
export const RESIZE_DEBOUNCE_MS = 200

const AccessConsentModal = ({
  heading,
  bodyContent,
  disclaimerText = null,
  agreeButtonLabel,
  redirectUrl = '/',
}: AccessConsentModalProps) => {
  const [isOpen, setIsOpen] = useState(true)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<ResizeObserver | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const router = useRouter()

  const {
    locale: { language: contextLanguage },
  } = useContext(SiteBuilderContext)

  const resolvedLocale: 'en' | 'fr' = normalizeLocale(contextLanguage)
  const cancelLabel = resolvedLocale === 'fr' ? 'Annuler' : 'Cancel'
  const redirectingLabel = resolvedLocale === 'fr' ? 'Redirection...' : 'Redirecting...'

  const checkScrollPosition = () => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer) return

    const isAtBottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight < SCROLL_THRESHOLD

    if (isAtBottom) {
      setHasScrolledToBottom(true)
      observerRef.current?.disconnect()
      observerRef.current = null
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }

  const onContentResize = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const el = scrollRef.current
      if (!el || el.scrollHeight === 0) return
      if (el.scrollHeight <= el.clientHeight) {
        // Content fits without scrolling
        setHasScrolledToBottom(true)
      } else {
        // Content requires scrolling — re-evaluate in case it grew after a short-content detection
        const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD
        setHasScrolledToBottom(isAtBottom)
      }
    }, RESIZE_DEBOUNCE_MS)
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const scrollCallbackRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    scrollRef.current = node
    if (!node) return

    observerRef.current = new ResizeObserver(onContentResize)
    observerRef.current.observe(node)
  }, [])

  const handleAccept = () => {
    setIsOpen(false)
  }

  const handleClose = async () => {
    setIsRedirecting(true)
    try {
      await router.push(redirectUrl)
    } catch (_error) {
      window.location.assign(redirectUrl)
    }
  }

  return (
    <Modal isOpen={isOpen} closeButton={null} tokens={{ maxWidth: '768px' }}>
      {isRedirecting ? (
        <LoadingContainer>
          <ActivityIndicator label={redirectingLabel} />
        </LoadingContainer>
      ) : (
        <>
          <HeadingContainer>
            <Typography variant={{ size: 'h3', colour: 'brand' }} tag="h3" block>
              {heading}
            </Typography>
          </HeadingContainer>

          <ScrollContainer
            ref={scrollCallbackRef}
            data-testid="access-consent-modal-scroll-container"
            onScroll={checkScrollPosition}
          >
            {bodyContent}
          </ScrollContainer>
          {disclaimerText && <DisclaimerContainer>{disclaimerText}</DisclaimerContainer>}
          <StackViewContainer>
            <StackView direction="row" space={4} tokens={{ alignItems: 'baseline' }}>
              <Button onPress={handleAccept} disabled={!hasScrolledToBottom} variant={{ priority: 'high' }}>
                {agreeButtonLabel}
              </Button>

              <Link onPress={handleClose} tokens={{ alignSelf: 'center' }}>
                {cancelLabel}
              </Link>
            </StackView>
          </StackViewContainer>
        </>
      )}
    </Modal>
  )
}

export default AccessConsentModal
